import React from "react";
import { useField } from "formik";
import styled from "styled-components";
import { TextAreaPropsWithoutRef } from "react-html-props";
import FieldErrorMessage from "./FieldErrorMessage";
import FieldLabel from "./FieldLabel";
import FieldHelpText from "./FieldHelpText";

const TextareaInputField = (props: Props) => {
  const { label, helpText, ...otherProps } = props;
  const [field, meta] = useField<string>(props);

  const showError = Boolean(meta.touched && meta.error);
  const htmlFor = otherProps.id || otherProps.name;

  const value = field.value || "";

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={htmlFor}>
        {label}
        {helpText && <FieldHelpText>{helpText}</FieldHelpText>}
      </FieldLabel>
      <Textarea
        id={htmlFor}
        {...field}
        {...otherProps}
        value={value}
        hasError={showError}
      />
      <Footer>
        <FieldErrorMessage name={otherProps.name} />
        {otherProps.maxLength && (
          <CharacterCount>
            {value.length} / {otherProps.maxLength} characters
          </CharacterCount>
        )}
      </Footer>
    </FieldWrapper>
  );
};

interface Props extends TextAreaPropsWithoutRef {
  label: string;
  name: string;
  helpText?: string;
}

const FieldWrapper = styled.div`
  font-family: ${({ theme }) => theme.font};
  margin-bottom: 1.5rem;
`;

const Textarea = styled.textarea<{
  hasError: boolean;
}>`
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  border-width: 2px;
  border-style: solid;
  border-color: ${({ hasError, theme }) =>
    hasError ? theme.colors.error : theme.colors.border};
  box-sizing: border-box;
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  box-shadow: none;
  font-weight: 500;
  min-height: 150px;

  :focus {
    outline: none;
    border-color: ${({ hasError, theme }) =>
      hasError ? theme.colors.error : theme.colors.primary};
  }

  ::placeholder {
    font-style: italic;
    color: ${({ theme }) => theme.colors.muted};
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 675px) {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
`;

const CharacterCount = styled.div`
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.muted};
`;

export default TextareaInputField;
